import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import { apiFetch } from "../utils/api";

const monthLabels = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const formatLocalDateOnly = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftLocalDateOnly = (isoDate, dayOffset) => {
  const [year, month, day] = String(isoDate || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + Number(dayOffset || 0));
  return formatLocalDateOnly(date);
};

const getDashboardDateAccessWindow = currentUser => {
  const policy = currentUser?.dateAccessPolicy?.dashboard || { backdateLimit: 1, futureLimit: 1 };
  const backdateLimit = Number.isFinite(Number(policy.backdateLimit)) ? Number(policy.backdateLimit) : 1;
  const futureLimit = Number.isFinite(Number(policy.futureLimit)) ? Number(policy.futureLimit) : 1;
  const today = formatLocalDateOnly(new Date());
  const allowedPastDays = backdateLimit === 0 ? null : Math.max(backdateLimit - 1, 0);
  const allowedFutureDays = futureLimit === 0 ? null : Math.max(futureLimit - 1, 0);
  return {
    minDate: allowedPastDays == null ? "" : shiftLocalDateOnly(today, -allowedPastDays),
    maxDate: allowedFutureDays == null ? "" : shiftLocalDateOnly(today, allowedFutureDays)
  };
};

const Dashboard = ({ currentUser, onOpenSyncedAppointment }) => {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [teamMonth, setTeamMonth] = useState(String(new Date().getMonth() + 1));
  const [teamDate, setTeamDate] = useState(new Date().toISOString().slice(0, 10));
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [monthlySales, setMonthlySales] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [cashCollectionAmount, setCashCollectionAmount] = useState(0);
  const [stockReport, setStockReport] = useState(null);
  const [bookingSummary, setBookingSummary] = useState({ pendingCount: 0, total: 0 });
  const [pendingBookings, setPendingBookings] = useState([]);
  const [bookingActionError, setBookingActionError] = useState("");
  const [bookingActionBusy, setBookingActionBusy] = useState("");
  const teamPerfRequestRef = useRef(0);
  const isTeamMember = currentUser?.role === "team_member";
  const dashboardDateWindow = useMemo(() => getDashboardDateAccessWindow(currentUser), [currentUser]);

  useEffect(() => {
    const branchQuery = branchFilter ? `&branchPublicId=${encodeURIComponent(branchFilter)}` : "";

    if (!isTeamMember) {
      apiFetch(`/api/reports/monthly-sales?year=${year}${branchQuery}`)
        .then(res => res.json())
        .then(data => setMonthlySales(Array.isArray(data) ? data : []))
        .catch(err => console.error("Failed to load monthly sales", err));
    } else {
      setMonthlySales([]);
    }

    apiFetch(`/api/reports/stock?year=${year}${branchQuery}`)
      .then(res => res.json())
      .then(setStockReport)
      .catch(err => console.error("Failed to load stock report", err));

    apiFetch("/api/customer-bookings/summary")
      .then(res => res.json())
      .then(data => setBookingSummary({ pendingCount: Number(data?.pendingCount || 0), total: Number(data?.total || 0) }))
      .catch(err => console.error("Failed to load booking summary", err));
  }, [year, branchFilter, isTeamMember]);

  useEffect(() => {
    const branchQuery = branchFilter ? `&branchPublicId=${encodeURIComponent(branchFilter)}` : "";
    apiFetch(`/api/customer-bookings?status=open${branchQuery}`)
      .then(res => res.json())
      .then(data => setPendingBookings(Array.isArray(data) ? data.slice(0, 6) : []))
      .catch(err => {
        console.error("Failed to load customer bookings", err);
        setPendingBookings([]);
      });
  }, [branchFilter]);

  const reviewBooking = async (booking, status, options = {}) => {
    const bookingId = booking?.publicId || booking?.id;
    if (!bookingId) return;
    const isAlreadyApproved = String(booking?.status || "").toLowerCase() === "approved";
    if (status === "approved" && isAlreadyApproved && options.openInAppointments) {
      const syncedRef = String(booking?.syncedWorkLogRef || "").trim();
      if (!syncedRef) {
        setBookingActionError("Approved booking has no linked appointment reference.");
        return;
      }
      if (typeof onOpenSyncedAppointment === "function") {
        await Promise.resolve(onOpenSyncedAppointment({
          workLogRef: syncedRef,
          appointmentDate: String(booking?.appointmentDate || "").trim()
        }));
      }
      return;
    }

    const reviewNote = status === "rejected"
      ? window.prompt("Optional rejection reason", "") || ""
      : "";

    setBookingActionError("");
    setBookingActionBusy(String(bookingId));
    try {
      const res = await apiFetch(`/api/customer-bookings/${bookingId}/review`, {
        method: "PATCH",
        body: JSON.stringify({ status, reviewNote })
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(payload?.error || `Request failed with ${res.status}`);
      }
      const saved = await res.json().catch(() => null);
      setPendingBookings(prev => {
        if (status === "rejected") {
          return prev.filter(item => String(item.publicId || item.id) !== String(bookingId));
        }
        return prev.map(item => (
          String(item.publicId || item.id) === String(bookingId)
            ? { ...item, ...saved, status: saved?.status || status, syncedWorkLogRef: saved?.syncedWorkLogRef || item.syncedWorkLogRef, isOpenRequest: true }
            : item
        ));
      });
      if (status === "rejected") {
        setBookingSummary(prev => ({
          ...prev,
          pendingCount: Math.max(0, Number(prev.pendingCount || 0) - 1)
        }));
      }

      if (status === "approved" && options.openInAppointments) {
        const syncedRef = String(saved?.syncedWorkLogRef || "").trim();
        if (!syncedRef) {
          throw new Error("Approved, but no synced appointment reference was returned.");
        }
        if (typeof onOpenSyncedAppointment === "function") {
          await Promise.resolve(onOpenSyncedAppointment({
            workLogRef: syncedRef,
            appointmentDate: String(saved?.appointmentDate || booking?.appointmentDate || "").trim()
          }));
        }
      }
    } catch (err) {
      setBookingActionError(err.message || "Failed to review booking request");
    } finally {
      setBookingActionBusy("");
    }
  };

  useEffect(() => {
    const branchQuery = branchFilter ? `&branchPublicId=${encodeURIComponent(branchFilter)}` : "";
    const teamMonthQuery = teamMonth ? `&month=${encodeURIComponent(teamMonth)}` : "";
    const teamDateQuery = teamDate ? `&date=${encodeURIComponent(teamDate)}` : "";
    const requestId = teamPerfRequestRef.current + 1;
    teamPerfRequestRef.current = requestId;

    setTeamPerformance([]);
    setCashCollectionAmount(0);
    apiFetch(`/api/reports/team-performance?year=${year}${teamMonthQuery}${teamDateQuery}${branchQuery}`)
      .then(res => res.json())
      .then(data => {
        if (teamPerfRequestRef.current !== requestId) return;
        setTeamPerformance(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (teamPerfRequestRef.current !== requestId) return;
        console.error("Failed to load team performance", err);
        setTeamPerformance([]);
      });

    apiFetch(`/api/reports/dashboard-kpis?year=${year}${teamMonthQuery}${teamDateQuery}${branchQuery}`)
      .then(res => res.json())
      .then(data => {
        if (teamPerfRequestRef.current !== requestId) return;
        setCashCollectionAmount(Number(data?.cashCollectionAmount || 0));
      })
      .catch(err => {
        if (teamPerfRequestRef.current !== requestId) return;
        console.error("Failed to load dashboard KPIs", err);
        setCashCollectionAmount(0);
      });
  }, [year, teamMonth, teamDate, branchFilter]);

  useEffect(() => {
    apiFetch("/api/branches")
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setBranches(list);
        if (list.length === 1 && list[0]?.publicId) {
          setBranchFilter(String(list[0].publicId));
        }
      })
      .catch(err => {
        console.error("Failed to load branches", err);
        setBranches([]);
      });
  }, []);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const baseYears = [current - 2, current - 1, current, current + 1].map(String);
    return baseYears.filter(yearValue => {
      if (dashboardDateWindow.minDate && Number(yearValue) < Number(String(dashboardDateWindow.minDate).slice(0, 4))) return false;
      if (dashboardDateWindow.maxDate && Number(yearValue) > Number(String(dashboardDateWindow.maxDate).slice(0, 4))) return false;
      return true;
    });
  }, [dashboardDateWindow]);

  useEffect(() => {
    if (dashboardDateWindow.minDate && teamDate < dashboardDateWindow.minDate) {
      setTeamDate(dashboardDateWindow.minDate);
    }
    if (dashboardDateWindow.maxDate && teamDate > dashboardDateWindow.maxDate) {
      setTeamDate(dashboardDateWindow.maxDate);
    }
  }, [dashboardDateWindow, teamDate]);

  const monthlyTotals = useMemo(() => {
    const map = new Map(monthlySales.map(m => [Number(m.month), m]));
    return monthLabels.map((label, idx) => {
      const rec = map.get(idx + 1);
      return {
        label,
        totalAmount: Number(rec?.totalAmount || 0),
        invoiceCount: Number(rec?.invoiceCount || 0)
      };
    });
  }, [monthlySales]);

  const maxMonthlySales = useMemo(() => {
    return Math.max(...monthlyTotals.map(item => item.totalAmount), 1);
  }, [monthlyTotals]);

  const topMembers = useMemo(() => {
    return [...teamPerformance].slice(0, 6);
  }, [teamPerformance]);

  const totalSalesAmount = useMemo(() => {
    return teamPerformance.reduce((sum, member) => sum + Number(member?.salesAmount || 0), 0);
  }, [teamPerformance]);

  const totalCollectionAmount = useMemo(() => {
    return teamPerformance.reduce((sum, member) => sum + Number(member?.collectionAmount || 0), 0);
  }, [teamPerformance]);

  const stockSummary = stockReport?.summary || {
    totalProducts: 0,
    lowStock: 0,
    expiringSoon: 0,
    expired: 0,
    stockValue: 0
  };

  const stockAlertTotal = Math.max(
    Number(stockSummary.lowStock || 0) + Number(stockSummary.expiringSoon || 0) + Number(stockSummary.expired || 0),
    1
  );

  return (
    <div className="dashboard-page page main-content">
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Monitor KPIs, sales trends, inventory alerts, and booking workload at a glance.</p>
        </div>
        <div className="dashboard-year">
          <label htmlFor="dashboard-branch-select">Branch</label>
          <select id="dashboard-branch-select" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
            {branches.length !== 1 && <option value="">All branches</option>}
            {branches.map(branch => (
              <option key={branch.publicId || branch.id} value={branch.publicId || ""}>
                {`${branch.branchCode || ""} - ${branch.branchName || "Branch"}`}
              </option>
            ))}
          </select>
          <label htmlFor="dashboard-year-select">Year</label>
          <select id="dashboard-year-select" value={year} onChange={e => setYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <p className="dashboard-date-policy-note">
        {dashboardDateWindow.minDate || dashboardDateWindow.maxDate
          ? `Dashboard date access: ${dashboardDateWindow.minDate || "no limit"} to ${dashboardDateWindow.maxDate || "no limit"}`
          : "Dashboard date access: no limit"}
      </p>

      <div className="dashboard-kpi-grid">
        <div className="dashboard-kpi-card"><span>Total Sales (Excl. VAT)</span><strong>{`AED ${Number(totalSalesAmount || 0).toFixed(2)}`}</strong></div>
        <div className="dashboard-kpi-card"><span>Collection (Incl. VAT)</span><strong>{`AED ${Number(totalCollectionAmount || 0).toFixed(2)}`}</strong></div>
        <div className="dashboard-kpi-card"><span>Cash Collection (Incl. VAT)</span><strong>{`AED ${Number(cashCollectionAmount || 0).toFixed(2)}`}</strong></div>
        <div className="dashboard-kpi-card"><span>Stock Value</span><strong>{`AED ${Number(stockSummary.stockValue || 0).toFixed(2)}`}</strong></div>
        <div className="dashboard-kpi-card"><span>Low Stock</span><strong>{stockSummary.lowStock}</strong></div>
        <div className="dashboard-kpi-card"><span>Expiring Soon</span><strong>{stockSummary.expiringSoon}</strong></div>
        <div className="dashboard-kpi-card"><span>Expired</span><strong>{stockSummary.expired}</strong></div>
        <div className="dashboard-kpi-card"><span>Open Online Bookings</span><strong>{bookingSummary.pendingCount}</strong></div>
      </div>

      <section className="dashboard-card">
        <h2>Online Booking Requests</h2>
        <p>Online bookings stay here until the linked appointment is invoiced or closed.</p>
        {bookingActionError && <p className="reports-error">{bookingActionError}</p>}
        {pendingBookings.length === 0 ? (
          <p>No open online booking requests.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="services-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingBookings.map(item => {
                  const key = item.publicId || item.id;
                  const busy = bookingActionBusy && String(bookingActionBusy) === String(key);
                  const isApproved = String(item.status || "").toLowerCase() === "approved";
                  return (
                    <tr key={key}>
                      <td data-label="Ref">{key}</td>
                      <td data-label="Client">{`${item.firstName || ""} ${item.lastName || ""}`.trim()}</td>
                      <td data-label="Date">{item.appointmentDate}</td>
                      <td data-label="Time">{`${item.startTime} - ${item.endTime}`}</td>
                      <td data-label="Mobile">{item.mobile}</td>
                      <td data-label="Status">{item.status || "pending"}</td>
                      <td data-label="Actions" className="actions-cell dashboard-booking-actions">
                        {!isApproved && <button className="dashboard-action-btn approve" disabled={busy} onClick={() => reviewBooking(item, "approved")}>Approve</button>}
                        <button className="dashboard-action-btn open" disabled={busy} onClick={() => reviewBooking(item, "approved", { openInAppointments: true })}>Open</button>
                        {!isApproved && <button className="dashboard-action-btn reject" disabled={busy} onClick={() => reviewBooking(item, "rejected")}>Reject</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className={`dashboard-grid ${isTeamMember ? "one-col" : "two-col"}`}>
        {!isTeamMember && (
          <section className="dashboard-card">
            <h2>Month-wise Sales</h2>
            <p>Graphical representation of sales trend for {year}</p>
            <div className="sales-chart-wrap">
              {monthlyTotals.map(item => (
                <div className="sales-bar-col" key={item.label}>
                  <div className="sales-bar-track" title={`${item.label}: AED ${item.totalAmount.toFixed(2)} (${item.invoiceCount} invoices)`}>
                    <div className="sales-bar-fill" style={{ height: `${(item.totalAmount / maxMonthlySales) * 100}%` }}></div>
                  </div>
                  <div className="sales-bar-label">{item.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="dashboard-card">
          <h2>Stock Expiry Alerts</h2>
          <p>Current stock risk distribution</p>
          <div className="alert-stack">
            <div className="alert-row">
              <span>Low Stock</span>
              <div className="alert-track">
                <div className="alert-fill low" style={{ width: `${(Number(stockSummary.lowStock || 0) / stockAlertTotal) * 100}%` }}></div>
              </div>
              <strong>{stockSummary.lowStock}</strong>
            </div>
            <div className="alert-row">
              <span>Expiring Soon</span>
              <div className="alert-track">
                <div className="alert-fill warning" style={{ width: `${(Number(stockSummary.expiringSoon || 0) / stockAlertTotal) * 100}%` }}></div>
              </div>
              <strong>{stockSummary.expiringSoon}</strong>
            </div>
            <div className="alert-row">
              <span>Expired</span>
              <div className="alert-track">
                <div className="alert-fill danger" style={{ width: `${(Number(stockSummary.expired || 0) / stockAlertTotal) * 100}%` }}></div>
              </div>
              <strong>{stockSummary.expired}</strong>
            </div>
          </div>
        </section>
      </div>

      <section className="dashboard-card team-card">
        <div className="team-card-header">
          <h2>Team Performance</h2>
          <div className="team-card-filters">
            <label htmlFor="team-performance-month-select">Month</label>
            <select
              id="team-performance-month-select"
              value={teamMonth}
              onChange={e => setTeamMonth(e.target.value)}
            >
              <option value="">All months</option>
              {monthLabels.map((monthLabel, index) => {
                const monthValue = String(index + 1);
                return <option key={monthValue} value={monthValue}>{monthLabel}</option>;
              })}
            </select>
            <label htmlFor="team-performance-year-select">Year</label>
            <select
              id="team-performance-year-select"
              value={year}
              onChange={e => setYear(e.target.value)}
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <label htmlFor="team-performance-date-input">Date</label>
            <input
              id="team-performance-date-input"
              type="date"
              value={teamDate}
              min={dashboardDateWindow.minDate || undefined}
              max={dashboardDateWindow.maxDate || undefined}
              onChange={e => {
                const nextDate = e.target.value;
                setTeamDate(nextDate);
                if (nextDate) {
                  const [nextYear, nextMonth] = nextDate.split("-");
                  if (nextYear) setYear(nextYear);
                  if (nextMonth) setTeamMonth(String(Number(nextMonth)));
                }
              }}
            />
          </div>
        </div>
        <p>Sales contribution by team members</p>

        {topMembers.length > 0 && (
          <div className="team-bars">
            {topMembers.map(member => (
              <div key={`${member.memberName}-bar`} className="team-bar-row">
                <div className="team-label">{member.memberName}</div>
                <div className="team-track">
                  <div className="team-fill" style={{ width: `${Math.min(100, Number(member.contributionPercent || 0))}%` }}></div>
                </div>
                <div className="team-meta">
                  {`${Number(member.contributionPercent || 0).toFixed(1)}% | AED ${Number(member.salesAmount || 0).toFixed(2)}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
